import React, { useState } from 'react';
import { sendPublicContactForm } from '../services/contactService';
import './Contact.css';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const TOPICS = [
  'Account question',
  'Health info question',
  'Technical help',
  'Billing',
  'Other',
];

const Contact: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !topic || !message.trim()) {
      setStatus('error');
      setErrorMsg('Please fill in all fields.');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      await sendPublicContactForm({
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
        _honeypot: honeypot,
      });
      setStatus('success');
      setName('');
      setEmail('');
      setTopic('');
      setMessage('');
      setHoneypot('');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="contact">
      <div className="contact-inner">
        {/* Header */}
        <div className="contact-header">
          <div className="contact-subtitle">We're here to help.</div>
          <div className="contact-phone">1-437-313-7554</div>
          <div className="contact-hours">Monday to Friday, 9:00 AM – 5:00 PM ET</div>
          <div className="contact-email">info@silverscribe.ca</div>
        </div>

        {/* Send Us a Message */}
        <div className="contact-card" style={{ maxWidth: '520px', margin: '0 auto' }}>
          <h3 className="contact-card-title">Send Us a Message</h3>

          {status === 'success' && (
            <div className="contact-alert contact-alert-success">
              Your message has been sent. We'll get back to you soon.
            </div>
          )}
          {status === 'error' && errorMsg && (
            <div className="contact-alert contact-alert-error">
              {errorMsg}
            </div>
          )}

          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Honeypot — hidden from real users */}
            <div className="contact-hp" aria-hidden="true">
              <label htmlFor="contact-hp-field">Leave this empty</label>
              <input
                id="contact-hp-field"
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="contact-field">
              <label className="contact-label">Your Name</label>
              <input
                className="contact-input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="contact-field">
              <label className="contact-label">Your Email</label>
              <input
                className="contact-input"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="contact-field">
              <label className="contact-label">What is this about?</label>
              <select
                className="contact-select"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              >
                <option value="">Select a topic...</option>
                {TOPICS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="contact-field">
              <label className="contact-label">Your Message</label>
              <textarea
                className="contact-textarea"
                placeholder="How can we help you?"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="contact-submit-btn"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
